import { useCallback, useRef } from 'react';
import type { DragInfo } from '../types';
import { useDragOverlayContext, useWorkbenchContext, useUIContext } from '../app/GameProviders';
import { CONSTANTS } from '../data.js';

export function useWorkbenchDrag(params: {
  onDragEnd: (info: DragInfo, instanceId: string) => void;
}) {
  const { onDragEnd } = params;
  const { setWorkbenchOverlay, workbenchOverlayRef, setIsHoveringInventory } = useDragOverlayContext();
  const { setDraggingInstanceId } = useWorkbenchContext();
  const { inventoryPanelRef } = useUIContext();
  const rafId = useRef(0);

  const startWorkbenchDrag = useCallback((e: React.PointerEvent, instanceId: string, itemId: string) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;

    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    setDraggingInstanceId(instanceId);
    // Set state once to make overlay active and set initial position
    setWorkbenchOverlay({
      active: true,
      itemId,
      instanceId,
      x: e.clientX,
      y: e.clientY
    });

    const checkInventoryHover = (clientX: number, clientY: number) => {
      const invEl = inventoryPanelRef.current;
      if (invEl) {
        const invRect = invEl.getBoundingClientRect();
        const isOverInventory = clientX >= invRect.left && clientX <= invRect.right && 
                               clientY >= invRect.top && clientY <= invRect.bottom;
        setIsHoveringInventory(isOverInventory);
      }
    };

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      
      // Check if hovering over inventory
      checkInventoryHover(ev.clientX, ev.clientY);
      
      // On move, update style directly via ref, throttled by rAF
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        if (!workbenchOverlayRef.current) {
          throw new Error('useWorkbenchDrag: workbenchOverlayRef is null during drag');
        }
        const size = CONSTANTS.WORKBENCH_ITEM_SIZE;
        const tx = Math.round(ev.clientX - size / 2);
        const ty = Math.round(ev.clientY - size / 2);
        workbenchOverlayRef.current.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      });
    };

    const finish = (ev: PointerEvent) => {
      ev.preventDefault();
      cancelAnimationFrame(rafId.current);

      const info: DragInfo = {
        point: { x: ev.clientX, y: ev.clientY },
        offset: { x: ev.clientX - startX, y: ev.clientY - startY }
      };

      onDragEnd(info, instanceId);

      setWorkbenchOverlay({ active: false, itemId: null, instanceId: null, x: 0, y: 0 });
      setDraggingInstanceId(null);
      setIsHoveringInventory(false);

      document.body.style.userSelect = prevUserSelect;

      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', finish, { passive: false });
    window.addEventListener('pointercancel', finish, { passive: false });
  }, [onDragEnd, setDraggingInstanceId, setWorkbenchOverlay, workbenchOverlayRef, setIsHoveringInventory, inventoryPanelRef]);

  return { startWorkbenchDrag };
}
